/**
=========================================================
* Vue Argon Dashboard 2 PRO - v3.0.0
=========================================================

* Product Page: https://creative-tim.com/product/vue-argon-dashboard-pro
* Copyright 2022 Creative Tim
* Coded by www.creative-tim.com
=========================================================
*/

import 'core-js/stable';
import 'regenerator-runtime/runtime';

import { createApp } from "vue";
import App from "./App.vue";
import store from "./store";
import router from "./router";

import "./assets/css/nucleo-icons.css";
import "./assets/css/nucleo-svg.css";

import VueTilt from "vue-tilt.js";
import VueSweetalert2 from "vue-sweetalert2";
import ArgonDashboard from "./argon-dashboard";
import eventBus from "./services/event-bus.service.js";
import { installAxiosAuthInterceptors } from "./services/axios-auth.service";
import {
  bootstrapRefreshSession,
  purgeAuthSessionForPublicAuthPage,
} from "./services/auth-session.service";
import { bootstrapLegacyTokenState, getAccessToken } from "./services/token.service";
import { isPublicAuthLocation } from "./utils/public-auth-routes";
import {
  beginSsoHandoff,
  hasSsoTokenQuery,
  redirectSsoTokenEntryIfNeeded,
} from "./utils/sso-entry";

import VueShepherdPlugin from "vue-shepherd";
import "shepherd.js/dist/css/shepherd.css";

const app = createApp(App);

if (typeof window !== "undefined") {
  window.__protegoGetAccessToken = getAccessToken;
  window.__protegoRouter = router;
}

const startApp = async () => {
  if (redirectSsoTokenEntryIfNeeded()) {
    return;
  }

  const hasIncomingSsoToken =
    typeof window !== "undefined" && hasSsoTokenQuery(window.location.search);

  if (hasIncomingSsoToken && typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    beginSsoHandoff(params.get("token"));
  }

  const onPublicAuthPage = isPublicAuthLocation() || hasIncomingSsoToken;

  bootstrapLegacyTokenState();
  installAxiosAuthInterceptors({ store, router });

  if (onPublicAuthPage) {
    const isSsoEntry =
      typeof window !== "undefined"
      && window.location.pathname === "/sso"
      && hasSsoTokenQuery(window.location.search);

    await purgeAuthSessionForPublicAuthPage({ allowServerRevoke: !isSsoEntry });
    store.commit("auth/isAuthenticated", false);
  } else {
    try {
      await bootstrapRefreshSession();
    } catch (error) {
      // bootstrap failures are handled by route/auth flows
    }

    if (getAccessToken()) {
      store.commit("auth/isAuthenticated", true);
    }
  }

  // install VueShepherd first (so `this.$shepherd` is available everywhere)
  app.use(VueShepherdPlugin);

  // install all the rest of your plugins
  app
      .use(store)
      .use(router)
      .use(VueTilt)
      .use(VueSweetalert2)
      .use(ArgonDashboard);

  // your globals
  app.config.globalProperties.$eventBus = eventBus;
  app.config.globalProperties.$isDemo = process.env.VUE_APP_IS_DEMO || 0;

  // finally mount once
  app.mount("#app");
};

startApp();
